// EDEN · the Document Viewer — Teacher Packs, required PDFs and exercise sheets, opened INSIDE EDEN.
//
// Teacher feedback (October 2026): "there should be an internal PDF reader tool within the web instead of
// external Adobe Acrobat", and the worksheet "reads like it is for a computer, not for teachers". Until now every
// pack and required PDF was a plain <a target=_blank>: Windows handed it to Acrobat, Android phones downloaded
// it, and the worksheet opened raw with its node ids, task families and pill codes on top.
//
// • initDocViewer(): one click listener. A same-origin link to a .pdf, to /materials/…_Exercises.html, or
//   carrying data-doc-open opens here instead of a new tab; a `download` attribute or data-no-viewer opts out.
//   The title is data-doc-title, else the link's words, else the file name made readable.
// • openDocument(): the full-screen viewer, on the Book Reader's patterns (book-reader.ts): role=dialog,
//   aria-modal, `inert` behind it, the phone's Back button and Escape close it, focus returns to the opener.
//     PDF   — drawn by PDF.js, vendored in public/vendor/pdfjs-4.10.38/ and imported the first time a PDF opens
//             (it is never part of the main bundle). Pages are stacked and drawn as they come near the screen;
//             − / + zoom, "Page 2 of 4", Download, and Print (every page as a picture, one per sheet).
//     sheet — the exercise sheet in a same-origin frame, cleaned for teachers by cleanSheet(); the sheet's own
//             "Print student copy / teacher key / everything" bar keeps working inside the frame.
// • mountPdfInline(): the same PDF view inside a side panel, with "Full screen".
//
// Messages are plain words and never red: 401 asks for sign-in, 404 says the file is not online yet, anything
// else offers "Try again" (and the file itself, when the server has it but the viewer could not draw it).

import "./styles/doc-viewer.css";
import { esc } from "./dom";
import { icon } from "./icons";
import { isBuildNote, noteWords, sentencesOf, setResolver, type NoteWords } from "./sheet-words";

/** Vendored PDF.js (see its README.txt). The version is in the path, so a new copy never mixes with a cached old one. */
const PDFJS_LIB = "/vendor/pdfjs-4.10.38/pdf.min.mjs";
const PDFJS_WORKER = "/vendor/pdfjs-4.10.38/pdf.worker.min.mjs";

export type DocKind = "pdf" | "sheet";
export interface DocOpenOptions { url: string; title?: string; page?: number; kind?: DocKind; opener?: HTMLElement | null }

// ── the part of PDF.js this file uses, typed here (PDF.js is vendored, not an npm dependency) ─────────────────
interface PdfViewport { width: number; height: number }
interface PdfRenderTask { promise: Promise<void>; cancel(): void }
interface PdfPage {
  getViewport(params: { scale: number }): PdfViewport;
  render(params: { canvasContext: CanvasRenderingContext2D; viewport: PdfViewport; transform?: number[]; intent?: "display" | "print" }): PdfRenderTask;
}
interface PdfDocument { numPages: number; getPage(pageNumber: number): Promise<PdfPage> }
interface PdfLoadingTask { promise: Promise<PdfDocument>; destroy(): Promise<void> }
interface PdfJs {
  GlobalWorkerOptions: { workerSrc: string };
  getDocument(src: { url: string; withCredentials: boolean; isEvalSupported: boolean }): PdfLoadingTask;
}

let pdfjs: Promise<PdfJs> | null = null;
/** PDF.js, imported once, the first time a teacher opens a PDF. A failed import is forgotten so "Try again" retries it. */
function loadPdfJs(): Promise<PdfJs> {
  pdfjs ??= (import(/* @vite-ignore */ PDFJS_LIB) as Promise<PdfJs>).then((lib) => {
    lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
    return lib;
  }, (error: unknown) => {
    pdfjs = null;
    throw error;
  });
  return pdfjs;
}

// ── names, kinds and messages ──────────────────────────────────────────────────────────────────────────────
const SHEET_PATH = /^\/materials\/.+_Exercises(?:\.html)?$/i;
const FILE_WORDS: Record<string, string> = { pack: "Teacher Pack", exercises: "Worksheet" };

function kindOf(pathname: string): DocKind | null {
  if (/\.pdf$/i.test(pathname)) return "pdf";
  return SHEET_PATH.test(pathname) ? "sheet" : null;
}

/** The address without its #fragment (same-origin ones as a path), and the page a `#page=3` asks for. */
function splitUrl(raw: string): { url: string; path: string; page: number | null } {
  const u = new URL(raw, location.href);
  const page = /(?:^#|&)page=(\d+)/i.exec(u.hash);
  u.hash = "";
  return { url: u.origin === location.origin ? u.pathname + u.search : u.href, path: u.pathname, page: page ? Number(page[1]) : null };
}

/** The file's own name, for Download ("G1_T1_W01_Pack.pdf"). An extension-less address gets one back: ".html" for a
 *  sheet, ".pdf" for a PDF (an uploaded file's address is often just an id). */
function fileName(url: string, kind?: DocKind, title?: string): string {
  const path = new URL(url, location.href).pathname;
  let name = path.slice(path.lastIndexOf("/") + 1);
  try { name = decodeURIComponent(name); } catch { /* keep it as it is */ }
  // An address that is only an id (an uploaded file: …/resources/<id>/file) is saved under the name the teacher gave it.
  const named = title?.replace(/[\\/:*?"<>|]+/g, " ").replace(/\s+/g, " ").trim().slice(0, 100);
  if (named && !SHEET_PATH.test(path) && !/\.[a-z0-9]{2,5}$/i.test(name)) name = named;
  name ||= "document";
  if (SHEET_PATH.test(path) && !/\.html?$/i.test(name)) name += ".html";
  else if (kind === "pdf" && !/\.[a-z0-9]{2,5}$/i.test(name)) name += ".pdf";
  return name;
}

/** A file name made readable: G1_T1_W01_Pack.pdf → "Teacher Pack" / "Grade 1 · Term 1 · Week 1". */
function describe(url: string): { name: string; context: string } {
  const stem = fileName(url).replace(/\.(?:pdf|html?)$/i, "");
  const id = /^G(\d{1,2})[-_]T(\d)[-_]W?(\d{1,2})(?:[-_](.+))?$/i.exec(stem);
  const words = (s: string): string => s.replace(/[-_]+/g, " ").replace(/\s+/g, " ").trim();
  if (id) {
    const rest = words(id[4] ?? "");
    return { name: FILE_WORDS[rest.toLowerCase()] ?? (rest || "File"), context: `Grade ${Number(id[1])} · Term ${Number(id[2])} · Week ${Number(id[3])}` };
  }
  const plain = words(stem);
  return { name: plain ? plain.charAt(0).toUpperCase() + plain.slice(1) : "Document", context: "" };
}

/** What a link says on the screen ("Teacher Pack, page 3"), without a trailing ↗. */
function visibleText(a: HTMLElement): string {
  return (a.innerText || a.textContent || "").replace(/\s+/g, " ").replace(/[\s↗›»→]+$/u, "").trim().slice(0, 140);
}

/** Why a file did not open, asked of the server the way book-reader.ts explainFailure() does. 0 = no connection. */
async function statusOf(url: string): Promise<number> {
  try {
    return (await fetch(url, { method: "HEAD", credentials: "same-origin", cache: "no-store" })).status;
  } catch {
    return 0;
  }
}

function troubleHtml(status: number, url: string, kind: DocKind, title?: string): string {
  if (status === 401) return `<p>Your sign-in has ended. Sign in again to open this file.</p><div class="dv-msg-row"><a class="dv-btn" href="/signin">Sign in again</a></div>`;
  if (status === 404) return `<p>This file is not online yet.</p>`;
  // The server has the file but the viewer could not show it (an older phone browser, or a file that is not a real
  // PDF): the connection is fine, so say that, and offer the file itself.
  if (status >= 200 && status < 300) {
    return `<p>This file cannot be shown here. Download it to open it on this device.</p><div class="dv-msg-row"><a class="dv-btn" href="${esc(url)}" download="${esc(fileName(url, kind, title))}" data-no-viewer>${icon("download", "ico-sm")}<span>Download the file</span></a><button type="button" class="dv-btn dv-quiet" data-dv="retry">Try again</button></div>`;
  }
  return `<p>The file did not open. Check the internet connection and try again.</p><div class="dv-msg-row"><button type="button" class="dv-btn" data-dv="retry">Try again</button></div>`;
}

// ── the PDF view: shared by the full-screen viewer and the inline panel ────────────────────────────────────────
const ZOOMS = [1, 1.25, 1.5, 2];
const ZOOM_WORDS = ["Fit", "125%", "150%", "200%"];
const GAP = 12; // the side padding of .dv-pages (doc-viewer.css)
const MAX_PIXELS = 12_000_000; // per canvas; iOS Safari refuses canvases over ~16.7 million pixels
// Drawn pages kept in memory: at most KEEP of them and BUDGET pixels in all (≈ 100 MB; older iPhones stop drawing
// canvases past a few hundred MB). Pages further away are let go, and drawn again when the teacher scrolls back.
const KEEP = 8;
const BUDGET = 25_000_000;

interface ViewState { page: number; total: number; zoom: number }
interface Slot {
  n: number;
  box: HTMLElement;
  size: PdfViewport | null; // the page at scale 1 (PDF points); null until known (long files)
  page: PdfPage | null;
  canvas: HTMLCanvasElement | null;
  drawn: string; // the key it was drawn at ("" = not drawn)
  task: PdfRenderTask | null;
}

function freeCanvas(c: HTMLCanvasElement): void {
  c.width = 0; // gives the memory back at once (Safari holds it otherwise)
  c.height = 0;
}

function isCancelled(error: unknown): boolean {
  return error instanceof Error && error.name === "RenderingCancelledException";
}

class PdfView {
  readonly list: HTMLElement;
  page = 1;
  total = 0;
  zoom = 0;
  ready = false;
  dead = false;
  private task: PdfLoadingTask | null = null;
  private doc: PdfDocument | null = null;
  private slots: Slot[] = [];
  private bySlot = new Map<Element, Slot>();
  private near = new Set<Slot>();
  private io: IntersectionObserver | null = null;
  private ro: ResizeObserver | null = null;
  private busy = false;
  private frame = 0;
  private width = 0;

  constructor(readonly scroller: HTMLElement, private readonly opts: { url: string; maxFit: number; onState(s: ViewState): void }) {
    this.list = document.createElement("div");
    this.list.className = "dv-pages";
    scroller.appendChild(this.list);
    scroller.addEventListener("scroll", this.onScroll, { passive: true });
  }

  async open(start: number): Promise<void> {
    const lib = await loadPdfJs();
    if (this.dead) return;
    this.task = lib.getDocument({ url: this.opts.url, withCredentials: true, isEvalSupported: false });
    const doc = await this.task.promise;
    if (this.dead) return;
    this.doc = doc;
    this.total = doc.numPages;
    const first = await doc.getPage(1);
    // A short file is measured page by page up front (exact layout); a long one assumes page 1's size until drawn.
    const rest = this.total <= 60 ? await Promise.all(Array.from({ length: this.total - 1 }, (_, i) => doc.getPage(i + 2))) : [];
    if (this.dead) return;
    const pages = [first, ...rest];
    for (let n = 1; n <= this.total; n++) {
      const box = document.createElement("div");
      box.className = "dv-page";
      box.setAttribute("role", "img");
      box.setAttribute("aria-label", `Page ${n} of ${this.total}`);
      const page = pages[n - 1] ?? null;
      const slot: Slot = { n, box, size: page ? page.getViewport({ scale: 1 }) : null, page, canvas: null, drawn: "", task: null };
      this.slots.push(slot);
      this.bySlot.set(box, slot);
      this.list.appendChild(box);
    }
    this.layout();
    this.ready = true;
    this.goTo(start);
    this.io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        const slot = this.bySlot.get(e.target);
        if (!slot) continue;
        if (e.isIntersecting) this.near.add(slot);
        else this.near.delete(slot);
      }
      this.pump();
    }, { root: this.scroller, rootMargin: "100% 0px" }); // draw a screen ahead, above and below
    for (const s of this.slots) this.io.observe(s.box);
    this.ro = new ResizeObserver(() => {
      if (this.scroller.clientWidth !== this.width && this.scroller.clientWidth > 0) this.relayout();
    });
    this.ro.observe(this.scroller);
  }

  zoomBy(step: number): void { this.setZoom(this.zoom + step); }

  setZoom(index: number): void {
    const next = Math.max(0, Math.min(ZOOMS.length - 1, index));
    if (next === this.zoom || !this.ready) return;
    this.zoom = next;
    this.relayout();
  }

  /** Scroll so page `n` is at the top. */
  goTo(n: number): void {
    const slot = this.slots[Math.max(1, Math.min(this.total, Math.round(n))) - 1];
    if (!slot) return;
    this.scroller.scrollTop = slot.box.offsetTop - GAP;
    this.page = slot.n;
    this.report();
  }

  /** Every page as a picture for printing (object URLs; the caller revokes them). */
  async printImages(): Promise<string[]> {
    const urls: string[] = [];
    const long = this.total > 20; // smaller pictures for a long file, so a phone does not run out of memory
    try {
      for (let n = 1; n <= this.total; n++) {
        const page = await this.pageAt(n);
        if (this.dead) break;
        let viewport = page.getViewport({ scale: long ? 2 : 2.5 }); // ≈ 144 / 180 dots per inch
        const pixels = viewport.width * viewport.height;
        if (pixels > MAX_PIXELS) viewport = page.getViewport({ scale: (long ? 2 : 2.5) * Math.sqrt(MAX_PIXELS / pixels) });
        const canvas = document.createElement("canvas");
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) throw new Error("canvas");
        await page.render({ canvasContext: ctx, viewport, intent: "print" }).promise;
        const blob = await new Promise<Blob | null>((done) => canvas.toBlob(done, long ? "image/jpeg" : "image/png", 0.92));
        freeCanvas(canvas);
        if (!blob) throw new Error("picture");
        urls.push(URL.createObjectURL(blob));
      }
    } catch (error) {
      for (const u of urls) URL.revokeObjectURL(u);
      throw error;
    }
    return urls;
  }

  /** The size of page 1 in PDF points (for the printed sheet). */
  firstSize(): PdfViewport | null { return this.slots[0]?.size ?? null; }

  destroy(): void {
    this.dead = true;
    this.ready = false;
    this.io?.disconnect();
    this.ro?.disconnect();
    this.scroller.removeEventListener("scroll", this.onScroll);
    cancelAnimationFrame(this.frame);
    for (const s of this.slots) {
      s.task?.cancel();
      if (s.canvas) freeCanvas(s.canvas);
    }
    this.slots = [];
    this.near.clear();
    this.bySlot.clear();
    this.list.remove();
    void this.task?.destroy().catch(() => undefined); // ends the PDF.js worker too
    this.task = null;
    this.doc = null;
  }

  private report(): void {
    this.opts.onState({ page: this.page, total: this.total, zoom: this.zoom });
  }

  private async pageAt(n: number): Promise<PdfPage> {
    const slot = this.slots[n - 1];
    if (slot?.page) return slot.page;
    const page = await this.doc!.getPage(n);
    if (slot) slot.page = page;
    return page;
  }

  /** Fit width: the scroller's width less the side padding, capped (a page as wide as a big monitor reads badly). */
  private fit(): number {
    const w = this.scroller.clientWidth;
    return Math.max(120, Math.min((w > 80 ? w : 360) - GAP * 2, this.opts.maxFit));
  }

  private layout(): void {
    this.width = this.scroller.clientWidth;
    const width = Math.round(this.fit() * (ZOOMS[this.zoom] ?? 1));
    const fallback = this.slots[0]?.size;
    for (const s of this.slots) {
      const size = s.size ?? fallback;
      if (!size) continue;
      s.box.style.width = `${width}px`;
      s.box.style.height = `${Math.round((width * size.height) / size.width)}px`;
    }
  }

  /** New sizes (zoom, a turned phone, a wider panel), keeping the same spot of the same page under the top edge. */
  private relayout(): void {
    const anchor = this.slots[this.page - 1];
    const sc = this.scroller;
    const down = anchor ? (sc.scrollTop - anchor.box.offsetTop) / Math.max(1, anchor.box.offsetHeight) : 0;
    const across = (sc.scrollLeft + sc.clientWidth / 2) / Math.max(1, sc.scrollWidth);
    this.layout();
    if (anchor) sc.scrollTop = anchor.box.offsetTop + down * anchor.box.offsetHeight;
    sc.scrollLeft = across * sc.scrollWidth - sc.clientWidth / 2;
    for (const s of this.slots) s.task?.cancel(); // a page being drawn at the old size
    this.report();
    this.pump();
  }

  private readonly onScroll = (): void => {
    if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.track(); });
  };

  /** "Page n": the page that fills most of the screen. */
  private track(): void {
    if (!this.ready) return;
    const top = this.scroller.scrollTop, bottom = top + this.scroller.clientHeight;
    let best = this.page, seen = -1;
    for (const s of this.slots) {
      const a = s.box.offsetTop, b = a + s.box.offsetHeight;
      if (b <= top) continue;
      if (a >= bottom) break;
      const visible = Math.min(b, bottom) - Math.max(a, top);
      if (visible > seen + 1) { best = s.n; seen = visible; }
    }
    if (best !== this.page) {
      this.page = best;
      this.report();
      this.pump();
    }
  }

  private key(s: Slot): string {
    return `${s.box.style.width}|${Math.min(window.devicePixelRatio || 1, 2.5)}`;
  }

  /** Draw one page at a time — the nearest page to the one being read first — so a phone stays responsive. */
  private pump(): void {
    if (this.busy || this.dead || !this.ready) return;
    let next: Slot | null = null;
    for (const s of this.near) {
      if (s.drawn === this.key(s)) continue;
      if (!next || Math.abs(s.n - this.page) < Math.abs(next.n - this.page)) next = s;
    }
    if (!next) return this.trim();
    this.busy = true;
    void this.draw(next).finally(() => {
      this.busy = false;
      this.pump();
    });
  }

  private async draw(s: Slot): Promise<void> {
    const key = this.key(s);
    try {
      const page = await this.pageAt(s.n);
      if (this.dead || key !== this.key(s)) return;
      if (!s.size) { // a long file: this page's real size is known only now
        s.size = page.getViewport({ scale: 1 });
        this.layout();
        if (key !== this.key(s)) return;
      }
      const cssWidth = parseFloat(s.box.style.width) || s.size.width;
      const viewport = page.getViewport({ scale: cssWidth / s.size.width });
      let out = Math.min(window.devicePixelRatio || 1, 2.5);
      const pixels = viewport.width * viewport.height * out * out;
      if (pixels > MAX_PIXELS) out *= Math.sqrt(MAX_PIXELS / pixels);
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.floor(viewport.width * out));
      canvas.height = Math.max(1, Math.floor(viewport.height * out));
      const ctx = canvas.getContext("2d");
      if (!ctx) { // no canvas memory left (iOS): leave this page blank rather than ask again and again
        s.drawn = key;
        return;
      }
      s.task = page.render({ canvasContext: ctx, viewport, transform: out === 1 ? undefined : [out, 0, 0, out, 0, 0] });
      await s.task.promise;
      if (this.dead || key !== this.key(s)) { freeCanvas(canvas); return; }
      if (s.canvas) freeCanvas(s.canvas);
      s.box.replaceChildren(canvas); // the old picture stayed (stretched) until this one was ready: no blank flash
      s.canvas = canvas;
      s.drawn = key;
    } catch (error) {
      if (!isCancelled(error)) s.drawn = key; // a page PDF.js cannot draw stays blank; the rest still come
    } finally {
      s.task = null;
    }
  }

  /** Let go of drawn pages far from the one being read, so a long file or a big zoom never fills a phone's memory. */
  private trim(): void {
    const drawn = this.slots.filter((s) => s.canvas);
    let count = drawn.length;
    let pixels = drawn.reduce((sum, s) => sum + s.canvas!.width * s.canvas!.height, 0);
    const away = drawn.filter((s) => !this.near.has(s)).sort((a, b) => Math.abs(b.n - this.page) - Math.abs(a.n - this.page));
    for (const s of away) {
      if (count <= KEEP && pixels <= BUDGET) break;
      pixels -= s.canvas!.width * s.canvas!.height;
      count--;
      freeCanvas(s.canvas!);
      s.canvas = null;
      s.drawn = "";
      s.box.replaceChildren();
    }
  }
}

// ── the exercise sheet, cleaned for teachers ───────────────────────────────────────────────────────────────────
const CLEAN_ID = "eden-dv-clean";
const CLEAN_CSS = `/* EDEN document viewer (src/doc-viewer.ts cleanSheet): the codes stay in the file, off the screen and the paper */
.pills, .crumb, .foot { display: none !important; }
.eden-tv button { min-height: 44px; }
.eden-src { margin: 10px 0 12px; }
.eden-src > summary { cursor: pointer; padding: 8px 0; min-height: 28px; color: #5b5b66; font-size: 0.9em; }
.eden-src[open] > summary { margin-bottom: 6px; }
.eden-build-notes { margin: 8px 0 0; padding: 8px 11px; border: 1px dashed var(--line, #d8d8de); border-radius: 5px;
  font: 9.5pt/1.5 "Helvetica Neue", Arial, sans-serif; color: var(--dim, #5b5b66); }
@media print { .eden-src { display: none !important; } }`;
/** A node id in running text — not one inside a file name ("assets-v1/G10-T1-11-Speaking-Photos.pdf") or a longer
 *  code ("G2-T2-08-ACCEPTED-CANDIDATE"). No look-behind: iOS Safari before 16.4 cannot parse one. */
const NODE_ID = /(^|[^\w/-])G(\d{1,2})-T(\d)-(\d{2})(?![\w-])/g;
const INLINE = new Set(["B", "STRONG", "I", "EM", "SPAN", "A", "CODE", "SMALL", "U"]);

// ── the teacher notes ("Before you teach this", the key's notes, the format line) in teacher words ─────────────
// The authored notes still carry some of the builder's evidence talk (teacher review, 15 Oct 2026; finding
// F-2026-10-15-01 asks for the real rewrite). On screen only — the file is never changed. The word rules live in
// sheet-words.ts (scripts/test-sheet-words.mjs). The student page's format line gets the same word rules but never
// loses a sentence. scripts/doc-viewer-browser.mjs checks, in a browser: the folding, the space after "Before you teach
// this:", the sheet's own code as "this week", the set names in the notes and the format line, a format line keeping
// its sentences, and a failed load of the set names being asked for again.
const TEACHER_NOTES = ".tnote, .kn, .fmt";
/** A sentence ends at . ! or ? (and its closing quote or bracket) before a space or the end of the text. */
const SENTENCE_END = /[.!?]["'’”)\]]*\s*$/;

/** Which grade and term a sheet belongs to, from its own (hidden) breadcrumb, cut line or title. */
function sheetHome(doc: Document): { grade: number; term: number; id: string } | null {
  const text = [doc.querySelector(".crumb")?.textContent, doc.querySelector(".cut")?.textContent, doc.title].join(" ");
  const id = /G(\d{1,2})-T(\d)-\d{2}/.exec(text);
  return id ? { grade: Number(id[1]), term: Number(id[2]), id: id[0] } : null;
}

/** "G1-T1-01 Exercises — LB1 …" → "Grade 1 Term 1 Week 1 worksheet — LB1 …" (the name a saved PDF gets); `week` is the
 *  week the sheet is for, when its cut line says so. */
function sheetTitle(raw: string, named?: string, week?: number): string {
  const id = /^\s*G(\d{1,2})-T(\d)-(\d{2})\s+Exercises\s*(?:[—–-]\s*)?(.*)$/i.exec(raw);
  if (!id) return raw;
  const what = `Grade ${Number(id[1])} Term ${Number(id[2])} Week ${week ?? Number(id[3])} worksheet`;
  const rest = named || id[4]?.trim();
  return rest ? `${what} — ${rest}` : what;
}

/**
 * Take the internal codes off an exercise sheet so it reads as a teacher's page: hide the task-family pills, the
 * breadcrumb and the generator's footer; "Student page — G1-T1-01, week 1 · name:" → "Student page · Week 1 · Name:";
 * the key's "ANSWER KEY, G1-T1-01 week 1" → "ANSWER KEY · Week 1"; any other node id in the text → "week 1" (or
 * "Term 2 week 5" / "Grade 6 Term 1 week 3" when it points elsewhere); the heading → `title`, when one is given.
 * The teacher notes are said in teacher words (sheet-words.ts); their word-set names need prepareSheet() first —
 * call readySheet() — or the set codes stay as written (a code in brackets still goes: "glad and guilty (PET-06)").
 * The file itself is not changed. Safe to call more than once.
 */
export function cleanSheet(doc: Document, title?: string): void {
  if (!doc.getElementById(CLEAN_ID)) {
    const style = doc.createElement("style");
    style.id = CLEAN_ID;
    style.textContent = CLEAN_CSS;
    (doc.head ?? doc.documentElement).appendChild(style);
  }
  const home = sheetHome(doc);
  const where = (g: number, t: number, w: number): string =>
    home && g === home.grade && t === home.term ? `week ${w}` : home && g === home.grade ? `Term ${t} week ${w}` : `Grade ${g} Term ${t} week ${w}`;
  const root = doc.body ?? doc.documentElement;
  const checking: string[] = []; // the builder's sentences taken off the teacher notes (kept below, folded)
  const words: NoteWords = {
    taskWords: (code) => taskWords(code),
    setTitle: setResolver(home ? setTitles.get(home.grade) : null),
    assetTitle: (file) => assetWords(file),
  };
  for (const note of doc.querySelectorAll<HTMLElement>(TEACHER_NOTES)) {
    if (note.dataset.edenClean || note.closest("script, style, .pills, .crumb, .foot")) continue; // once per note
    note.dataset.edenClean = "1";
    const folds = note.matches(".tnote, .kn"); // the student page's format line keeps every sentence
    let said = ""; // the note's text before this piece: does the piece open a sentence?
    const pieces: Text[] = [];
    const inNote = doc.createTreeWalker(note, NodeFilter.SHOW_TEXT);
    for (let n = inNote.nextNode(); n; n = inNote.nextNode()) pieces.push(n as Text);
    for (const piece of pieces) {
      const text = piece.nodeValue ?? "";
      const opens = said.trim() === "" || /[.!?:]["'’”)\]]*\s*$/.test(said);
      said += text;
      const kept: string[] = [];
      sentencesOf(text).forEach((sentence, i) => {
        // Only a WHOLE sentence folds — one that starts and ends inside this piece of text. Half a sentence beside a
        // bold or italic word stays, so the note never loses the start or the end of what it says.
        if (folds && (i > 0 || opens) && SENTENCE_END.test(sentence) && isBuildNote(sentence)) checking.push(sentence.trim());
        else kept.push(noteWords(sentence, words));
      });
      let out = kept.join("");
      const lead = /^\s*/.exec(text)![0]; // "Before you teach this: This node resolves…. Live…" keeps the space after the colon
      if (lead && !/^\s/.test(out)) out = lead + out;
      if (out !== text) piece.nodeValue = out;
    }
    if (folds && !note.textContent?.trim()) note.style.display = "none"; // a note that was only a build record: no empty box
  }
  // The builder's source record ("LGB1-SB p.4-5 — Student Book printed/PDF pages 4-5 …", shelf ids, graph counts) is
  // for checking the sheet, not for teaching from it (teacher review, 15 Oct 2026: "like for a computer to read"): it
  // folds into one quiet line, and stays off the paper. The lesson page shows the same book pages in plain words.
  const src = doc.querySelector<HTMLElement>(".front .src");
  let fold = doc.querySelector<HTMLDetailsElement>("details.eden-src");
  if (!fold && (src || checking.length)) {
    fold = doc.createElement("details");
    fold.className = "eden-src";
    const summary = doc.createElement("summary");
    summary.textContent = "Where this worksheet comes from (for checking)";
    fold.appendChild(summary);
    if (src) { src.replaceWith(fold); fold.appendChild(src); }
    else (doc.querySelector(".front") ?? doc.querySelector(".sheet") ?? root).appendChild(fold);
  }
  // the builder's sentences taken off the teacher notes above, as the sheet has them — only the week codes are put in
  // words, by the pass below, like everywhere else on the page
  if (fold && checking.length) {
    const notes = doc.createElement("p");
    notes.className = "eden-build-notes";
    notes.textContent = `Build notes: ${checking.map((x) => x.trim()).join(" ")}`;
    fold.appendChild(notes);
  }
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const parent = node.parentElement;
    if (!parent || parent.closest("script, style, .pills, .crumb, .foot")) continue;
    const before = node.nodeValue ?? "";
    let text = before;
    if (parent.closest(".cut")) {
      text = text.replace(/\s*[—–-]\s*G\d{1,2}-T\d-\d{2}\s*,\s*week\s+(\d+)/i, " · Week $1").replace(/\bname:/, "Name:");
    } else if (parent.closest(".kh")) {
      text = text.replace(/,\s*G\d{1,2}-T\d-\d{2}\s+week\s+(\d+)/i, " · Week $1");
    }
    // In the teacher notes the sheet's own code is "this week": in a few Grade 1 Term 2 sheets the code's number is not
    // the week the sheet is for (G1-T2-13 is taught in week 12), so "week 13" there would send a teacher to the wrong week.
    const notes = !!parent.closest(".tnote, .kn");
    const said = (g: number, t: number, w: number): string => (notes && home?.id === `G${g}-T${t}-${String(w).padStart(2, "0")}` ? "this week" : where(g, t, w));
    // "week 2 (G2-T1-02, Student Book page 6)" says the week twice once the code is in words: the code goes
    text = text.replace(/(\bweeks? \d+(?:(?:,| and| to|–|-) ?\d+)*)('s)? \((G\d{1,2}-T\d-\d{2}(?:(?:,| and) ?G\d{1,2}-T\d-\d{2})*)(\)|,\s*)/g,
      (m: string, weeks: string, poss: string | undefined, ids: string, close: string) => {
        const nums = new Set((weeks.match(/\d+/g) ?? []).map(Number));
        const same = [...ids.matchAll(/G(\d{1,2})-T(\d)-(\d{2})/g)].every((x) => said(Number(x[1]), Number(x[2]), Number(x[3])) === `week ${Number(x[3])}` && nums.has(Number(x[3])));
        return same ? `${weeks}${poss ?? ""}${close === ")" ? "" : " ("}` : m;
      });
    text = text.replace(NODE_ID, (_m: string, lead: string, g: string, t: string, w: string, at: number, all: string) => {
      const plain = said(Number(g), Number(t), Number(w));
      // A sentence that starts with the code starts with a capital ("Week 2 opens Module 1").
      const opens = !INLINE.has(parent.tagName) && /^\s*$|[.!?]\s*$/.test(all.slice(0, at + lead.length));
      return lead + (opens ? plain.charAt(0).toUpperCase() + plain.slice(1) : plain);
    });
    if (text !== before) node.nodeValue = text;
  }
  const named = title?.trim();
  const heading = doc.querySelector(".front h1") ?? doc.querySelector("h1");
  // No title given: the sheet's own heading, with its codes spelled out the way the year plan shows them
  // ("LB1 Let's Get Ready!" → "Let's Begin 1 · Let's Get Ready!", "U4 Around Town" → "Unit 4 · Around Town") by the
  // app's own title words (initDocViewer's cleanTitle — the same lightTitle + plainText the year plan uses).
  const shown = named || (heading ? titleWords(heading.textContent?.trim() ?? "") : "");
  if (shown && heading) heading.textContent = shown;
  // The week the sheet is FOR (its cut line), not its code's number: four Grade 1 Term 2 sheets differ (G1-T2-12 is
  // taught in week 15), and this is the name a saved PDF gets.
  const week = /\bweek\s+(\d+)/i.exec(doc.querySelector(".cut")?.textContent ?? "")?.[1];
  doc.title = sheetTitle(doc.title, shown || undefined, week ? Number(week) : undefined);
}

// ── the full-screen viewer ─────────────────────────────────────────────────────────────────────────────────────
const st = {
  kind: "pdf" as DocKind,
  url: "",
  name: "",
  sub: "",
  headline: undefined as string | undefined, // a sheet's new heading: only a title the caller gave on purpose
  start: 1,
  view: null as PdfView | null,
  frame: null as HTMLIFrameElement | null,
  opener: null as HTMLElement | null,
  returnFocus: null as HTMLElement | null,
  pushed: false,
  closingByButton: false,
  inerted: [] as HTMLElement[],
  gen: 0,
  printed: [] as string[], // object URLs of the print pictures, kept while this file is open
  printing: false,
  savedTitle: null as string | null,
};

let root: HTMLElement | null = null;
const $ = <T extends HTMLElement = HTMLElement>(sel: string): T => root!.querySelector<T>(sel)!;

function build(): HTMLElement {
  const el = document.createElement("div");
  el.id = "eden-docview";
  el.className = "docview";
  el.hidden = true;
  el.setAttribute("role", "dialog");
  el.setAttribute("aria-modal", "true");
  el.setAttribute("aria-labelledby", "dv-name");
  el.innerHTML = `
    <header class="dv-bar">
      <button type="button" class="dv-back" data-dv="close" aria-label="Close and go back">${icon("back")}<span>Back</span></button>
      <div class="dv-title"><b class="dv-name" id="dv-name"></b><span class="dv-sub"></span></div>
      <div class="dv-tools">
        <div class="dv-zoom">
          <button type="button" class="dv-tool" data-dv="zoom-out" aria-label="Make the page smaller">−</button>
          <span class="dv-level" aria-live="polite"></span>
          <button type="button" class="dv-tool" data-dv="zoom-in" aria-label="Make the page bigger">+</button>
        </div>
        <div class="dv-files">
          <a class="dv-tool dv-word" data-dv-download href="#" download>${icon("download", "ico-sm")}<span>Download</span></a>
          <button type="button" class="dv-tool dv-word" data-dv="print">${icon("print", "ico-sm")}<span>Print</span></button>
        </div>
      </div>
    </header>
    <div class="dv-body">
      <div class="dv-scroll" data-dv-scroll tabindex="0" role="region" aria-label="Pages" hidden></div>
      <p class="dv-count" hidden></p>
      <p class="dv-note" role="status" hidden></p>
      <div class="dv-msg" role="status" hidden></div>
    </div>`;
  document.body.appendChild(el);
  el.addEventListener("click", (e) => {
    const b = (e.target as Element).closest<HTMLElement>("[data-dv]");
    if (!b) return;
    switch (b.dataset.dv) {
      case "close": return close();
      case "zoom-in": return st.view?.zoomBy(1);
      case "zoom-out": return st.view?.zoomBy(-1);
      case "print": return void print();
      case "retry": return start();
    }
  });
  return el;
}

/** Open a file in the full-screen viewer. A file already open is replaced in place. */
export function openDocument(opts: DocOpenOptions): void {
  show(opts, !!opts.title?.trim());
}

function show(opts: DocOpenOptions, titleGiven: boolean): void {
  listen();
  root ??= build();
  const { url, path, page } = splitUrl(opts.url);
  teardown();
  const names = describe(url);
  st.kind = opts.kind ?? kindOf(path) ?? "pdf";
  st.url = url;
  st.start = Math.max(1, Math.floor(opts.page ?? page ?? 1));
  st.name = opts.title?.trim() || names.name;
  st.sub = names.context;
  st.headline = titleGiven ? opts.title?.trim() : undefined;
  if (root.hidden) {
    st.returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    root.hidden = false;
    document.body.classList.add("docview-open");
    st.inerted = [];
    for (const id of ["app", "eden-nav", "eden-tabbar", "eden-reader"]) {
      const el = document.getElementById(id);
      if (el && !el.hasAttribute("inert")) { el.setAttribute("inert", ""); st.inerted.push(el); }
    }
    // the phone's Back button closes the viewer instead of leaving the page underneath
    if (!st.pushed) {
      history.pushState({ ...(history.state ?? {}), edenDocView: true }, "", location.href);
      st.pushed = true;
    }
  }
  if (opts.opener !== undefined) st.opener = opts.opener;
  root.dataset.kind = st.kind;
  $(".dv-name").textContent = st.name;
  $(".dv-sub").textContent = st.sub;
  $(".dv-sub").hidden = !st.sub;
  const download = $<HTMLAnchorElement>("[data-dv-download]");
  download.href = url;
  download.download = fileName(url, st.kind, st.name);
  start();
  $(".dv-back").focus();
}

/** (Re)start drawing the current file. */
function start(): void {
  teardown();
  const gen = ++st.gen;
  $<HTMLButtonElement>('[data-dv="print"]').disabled = true;
  message(`<p>Opening ${st.kind === "sheet" ? "the worksheet" : "the file"}…</p>`);
  if (st.kind === "pdf") void startPdf(gen);
  else startSheet(gen);
}

async function startPdf(gen: number): Promise<void> {
  const scroller = $("[data-dv-scroll]");
  scroller.hidden = false;
  const view = new PdfView(scroller, { url: st.url, maxFit: 900, onState: (s) => { if (st.view === view) paintState(s); } });
  st.view = view;
  paintState({ page: 1, total: 0, zoom: 0 });
  try {
    await view.open(st.start);
    if (gen !== st.gen || view.dead) return;
    message(null);
    $(".dv-count").hidden = false;
    $<HTMLButtonElement>('[data-dv="print"]').disabled = false;
  } catch {
    if (gen !== st.gen || view.dead) return;
    view.destroy();
    st.view = null;
    scroller.hidden = true;
    await explain(gen);
  }
}

function paintState(s: ViewState): void {
  $(".dv-count").textContent = s.total ? `Page ${s.page} of ${s.total}` : "";
  $(".dv-level").textContent = ZOOM_WORDS[s.zoom] ?? "";
  $<HTMLButtonElement>('[data-dv="zoom-out"]').disabled = !s.total || s.zoom <= 0;
  $<HTMLButtonElement>('[data-dv="zoom-in"]').disabled = !s.total || s.zoom >= ZOOMS.length - 1;
}

function startSheet(gen: number): void {
  const frame = document.createElement("iframe");
  frame.className = "dv-frame dv-wait"; // invisible until it is checked and cleaned
  frame.title = st.name;
  frame.src = st.url; // set before it joins the page: the first load adds no history entry, so Back still closes
  frame.addEventListener("load", () => void sheetLoaded(frame, gen));
  const body = $(".dv-body");
  body.insertBefore(frame, body.firstChild); // (not prepend(): @cloudflare/workers-types retypes Element.prepend)
  st.frame = frame;
}

async function sheetLoaded(frame: HTMLIFrameElement, gen: number): Promise<void> {
  if (gen !== st.gen || st.frame !== frame) return;
  let doc: Document | null = null;
  try { doc = frame.contentDocument; } catch { doc = null; } // a browser error page is another origin
  if (doc && doc.URL === "about:blank") return;
  if (doc?.querySelector(".sheet")) {
    await readySheet(doc, st.headline); // the frame stays invisible (dv-wait) until its notes are in teacher words
    if (gen !== st.gen || st.frame !== frame) return;
    doc.addEventListener("keydown", onKey); // Escape and Ctrl+P while the sheet has the focus
    frame.classList.remove("dv-wait");
    message(null);
    $<HTMLButtonElement>('[data-dv="print"]').disabled = false;
    return;
  }
  // Not a sheet: the gate's "Please sign in." or "Not found", or no connection. Ask why, then say it plainly.
  frame.classList.add("dv-wait");
  await explain(gen);
}

async function explain(gen: number): Promise<void> {
  const status = await statusOf(st.url);
  if (gen !== st.gen) return;
  st.frame?.remove();
  st.frame = null;
  message(troubleHtml(status, st.url, st.kind, st.name), true);
}

/** The message over the stage. `trouble` hides the bar's tools: there is nothing to zoom, print or download. */
function message(html: string | null, trouble = false): void {
  const box = $(".dv-msg");
  box.hidden = html === null;
  box.innerHTML = html ?? "";
  root?.toggleAttribute("data-trouble", trouble);
}

/** Print: a sheet prints itself (its frame); a PDF prints as one picture per sheet of paper. */
async function print(): Promise<void> {
  if (!root || root.hidden) return;
  if (st.kind === "sheet") {
    const win = st.frame && !st.frame.classList.contains("dv-wait") ? st.frame.contentWindow : null;
    win?.focus();
    win?.print();
    return;
  }
  const view = st.view;
  if (!view?.ready || st.printing) return;
  const gen = st.gen;
  const button = $<HTMLButtonElement>('[data-dv="print"]');
  const note = $(".dv-note");
  st.printing = true;
  button.disabled = true;
  try {
    if (!st.printed.length) {
      note.hidden = false;
      note.textContent = view.total > 1 ? `Getting ${view.total} pages ready to print…` : "Getting the page ready to print…";
      const urls = await view.printImages();
      if (gen !== st.gen) { for (const u of urls) URL.revokeObjectURL(u); return; }
      st.printed = urls;
      const box = document.createElement("div");
      box.id = "eden-dv-print";
      box.setAttribute("aria-hidden", "true");
      box.innerHTML = urls.map((u) => `<div class="dv-print-page"><img alt="" src="${esc(u)}"></div>`).join("");
      document.body.appendChild(box);
      await Promise.all([...box.querySelectorAll("img")].map((img) => img.decode().catch(() => undefined)));
      if (gen !== st.gen) return;
    }
    // The app's own @page rules (A4, 2 cm margins, page numbers) are for its forms; a PDF page already has its margins.
    // The pictures stay ready while the file is open (a phone may lay the print out after print() returns), and
    // all of it is taken away when the viewer closes (teardown).
    const size = view.firstSize();
    let page = document.getElementById("eden-dv-page");
    if (!page) {
      page = document.createElement("style");
      page.id = "eden-dv-page";
      document.head.appendChild(page);
    }
    page.textContent = `@page { ${size ? `size: ${size.width.toFixed(2)}pt ${size.height.toFixed(2)}pt !important; ` : ""}margin: 0 !important;
      @top-left { content: none } @top-center { content: none } @top-right { content: none }
      @bottom-left { content: none } @bottom-center { content: none } @bottom-right { content: none } }`;
    document.documentElement.classList.add("dv-printing");
    st.savedTitle ??= document.title;
    document.title = st.sub ? `${st.name} — ${st.sub}` : st.name; // the name "Save as PDF" offers
    note.hidden = true;
    window.print();
  } catch {
    if (gen === st.gen) {
      note.hidden = false;
      note.textContent = "The pages did not get ready to print. Try again.";
      window.setTimeout(() => { if (gen === st.gen) note.hidden = true; }, 4000);
    }
  } finally {
    st.printing = false;
    if (gen === st.gen) button.disabled = false;
  }
}

function restoreTitle(): void {
  if (st.savedTitle === null) return;
  document.title = st.savedTitle;
  st.savedTitle = null;
}

/** Stop drawing, unload the frame, take the print pictures away. */
function teardown(): void {
  st.view?.destroy();
  st.view = null;
  st.frame?.remove(); // removing the frame unloads the sheet
  st.frame = null;
  st.printing = false;
  document.documentElement.classList.remove("dv-printing");
  document.getElementById("eden-dv-page")?.remove();
  document.getElementById("eden-dv-print")?.remove();
  for (const u of st.printed) URL.revokeObjectURL(u);
  st.printed = [];
  restoreTitle();
  if (!root) return;
  $("[data-dv-scroll]").hidden = true;
  $(".dv-count").hidden = true;
  $(".dv-note").hidden = true;
  message(null);
}

function close(fromHistory = false): void {
  if (!root || root.hidden) return;
  st.gen++;
  teardown();
  root.hidden = true;
  document.body.classList.remove("docview-open");
  for (const el of st.inerted) el.removeAttribute("inert");
  st.inerted = [];
  if (st.pushed && !fromHistory) {
    st.closingByButton = true;
    history.back();
  }
  st.pushed = false;
  const back = st.opener?.isConnected ? st.opener : st.returnFocus?.isConnected ? st.returnFocus : null;
  st.opener = null;
  st.returnFocus = null;
  back?.focus();
}

function isTyping(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || /^(?:INPUT|TEXTAREA|SELECT)$/.test(target.tagName));
}

/** Escape closes; Ctrl/⌘+P prints the file (not the screen behind it); + and − zoom a PDF. */
function onKey(e: KeyboardEvent): void {
  if (!root || root.hidden) return;
  if (e.key === "Escape") {
    e.preventDefault();
    e.stopImmediatePropagation(); // the Book Reader underneath stays open
    close();
  } else if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === "p") {
    e.preventDefault();
    e.stopImmediatePropagation();
    void print();
  } else if (st.kind === "pdf" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTyping(e.target) && (e.key === "+" || e.key === "=" || e.key === "-")) {
    e.preventDefault();
    e.stopImmediatePropagation();
    st.view?.zoomBy(e.key === "-" ? -1 : 1);
  }
}

/** The viewer's own history entry: Back closes it, and nothing behind it (the router, the Book Reader) sees that. */
function onPop(e: PopStateEvent): void {
  if (st.closingByButton) {
    st.closingByButton = false;
    e.stopImmediatePropagation();
    return;
  }
  if (root && !root.hidden) {
    st.pushed = false;
    close(true);
    e.stopImmediatePropagation();
  }
}

let listening = false;
/** Keys and Back, once. Keys listen in the capture phase, so the viewer (the top layer) answers before the Book
 *  Reader's keys. popstate fires on `window` itself, where browsers run listeners in the order they were ADDED
 *  (capture or not; measured in Chromium 141) — hence initDocViewer() at boot, before initBookReader() and initRouter(). */
function listen(): void {
  if (listening) return;
  listening = true;
  document.addEventListener("keydown", onKey, true);
  window.addEventListener("popstate", onPop);
  window.addEventListener("afterprint", restoreTitle);
}

let started = false;
/** How a sheet's own heading is put into teacher words when no title is given. The app passes its year-plan title
 *  words (main.ts); by itself (scripts/doc-viewer-browser.mjs) the viewer leaves the heading as written. Kept as an
 *  option so this file never imports the curriculum code (it is bundled alone for its own browser check). */
let titleWords: (raw: string) => string = (raw) => raw;
/** The words for a Cambridge task-family code (the app passes lesson-data's taskLabel); alone, the code stays. */
let taskWords: (code: string) => string = () => "";
/** The lesson page's name for a worksheet's companion PDF (the app passes lesson-data's companionTitle). */
let assetWords: (file: string) => string | undefined = () => undefined;
/** A grade's word-set names as the lesson page shows them ("PET-01" → "Work & Jobs I"; "" for a set with no words).
 *  The app passes a loader (main.ts); alone, the codes stay as written. */
let setLoader: (grade: number) => Promise<ReadonlyMap<string, string> | null> = async () => null;
const setTitles = new Map<number, ReadonlyMap<string, string> | null>();

/** Make sure a grade's word-set names are at hand before a sheet of that grade is cleaned (cleanSheet is synchronous).
 *  A failed load is not kept: the next sheet asks the loader again. (The app's loader downloads a module; when that
 *  download fails — the connection dropped, or an app update replaced the file — a browser may keep the failure until
 *  the page is reloaded, as Chromium does, and then the set codes stay as written until the app is reloaded.) */
export async function prepareSheet(grade: number): Promise<void> {
  if (setTitles.has(grade)) return;
  try { setTitles.set(grade, await setLoader(grade)); }
  catch { /* the names could not be loaded: the set codes stay as written on this sheet */ }
}

/** cleanSheet() once the sheet's grade has its word-set names: what the viewer and the planning page call. */
export async function readySheet(doc: Document, title?: string): Promise<void> {
  const home = sheetHome(doc);
  if (home) await prepareSheet(home.grade);
  cleanSheet(doc, title);
}

/** Once, at boot — BEFORE initBookReader() and initRouter(), so the phone's Back closes the viewer first and the
 *  router never re-renders the page under it (the same rule book-reader.ts follows). */
export function initDocViewer(opts: {
  cleanTitle?: (raw: string) => string;
  taskWords?: (code: string) => string;
  assetTitle?: (file: string) => string | undefined;
  setTitles?: (grade: number) => Promise<ReadonlyMap<string, string> | null>;
} = {}): void {
  if (opts.cleanTitle) titleWords = opts.cleanTitle;
  if (opts.taskWords) taskWords = opts.taskWords;
  if (opts.assetTitle) assetWords = opts.assetTitle;
  if (opts.setTitles) setLoader = opts.setTitles;
  if (started) return;
  started = true;
  listen();
  document.addEventListener("click", (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target instanceof Element ? e.target.closest("a[href]") : null;
    if (!(a instanceof HTMLAnchorElement) || a.hasAttribute("download") || a.closest("[data-no-viewer]") || a.hasAttribute("data-link")) return;
    let u: URL;
    try { u = new URL(a.href, location.href); } catch { return; }
    if (u.origin !== location.origin) return;
    const asked = a.dataset.docOpen; // data-doc-open, optionally ="pdf" or ="sheet"
    const kind: DocKind | null = asked === "pdf" || asked === "sheet"
      ? asked
      : kindOf(u.pathname) ?? (asked === undefined ? null : /\.html?$/i.test(u.pathname) ? "sheet" : "pdf");
    if (!kind) return;
    e.preventDefault();
    const named = a.dataset.docTitle?.trim();
    // Only data-doc-title may replace a sheet's heading: the link's own words are often just "Worksheet".
    show({ url: u.href, title: named || visibleText(a) || describe(u.href).name, kind, opener: a }, !!named);
  });
}

// ── the inline view, for a side panel ──────────────────────────────────────────────────────────────────────────
/** "Full screen" — four corners, drawn like the set in icons.ts (which has no such glyph). */
const EXPAND = `<svg class="ico ico-sm" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M4.5 9V4.5H9M15 4.5h4.5V9M19.5 15v4.5H15M9 19.5H4.5V15"/></svg>`;

/**
 * The PDF view inside `container` (a side panel ~420px wide, or a phone column): − / +, "Page n of N", and
 * "Full screen", which opens the same file at the same page in the full-screen viewer. Its height is
 * `--dv-inline-height` (default: most of the screen). Call destroy() when the panel closes.
 */
export function mountPdfInline(container: HTMLElement, url: string, opts: { page?: number; title?: string } = {}): { destroy(): void } {
  const target = splitUrl(url);
  const name = opts.title?.trim() || describe(target.url).name;
  const box = document.createElement("div");
  box.className = "dv-inline";
  box.innerHTML = `
    <div class="dv-ibar" role="toolbar" aria-label="${esc(name)}">
      <button type="button" class="dv-tool" data-dvi="zoom-out" aria-label="Make the page smaller" disabled>−</button>
      <span class="dv-level" aria-live="polite">Fit</span>
      <button type="button" class="dv-tool" data-dvi="zoom-in" aria-label="Make the page bigger" disabled>+</button>
      <span class="dv-icount"></span>
      <button type="button" class="dv-tool dv-word" data-dvi="full" aria-label="Open ${esc(name)} full screen">${EXPAND}<span>Full screen</span></button>
    </div>
    <div class="dv-iscroll" tabindex="0" role="region" aria-label="${esc(name)}"></div>
    <div class="dv-msg" role="status" hidden></div>`;
  container.appendChild(box);
  const q = <T extends HTMLElement = HTMLElement>(sel: string): T => box.querySelector<T>(sel)!;
  let view: PdfView | null = null;
  let gone = false;

  const paint = (s: ViewState): void => {
    q(".dv-icount").textContent = s.total ? `Page ${s.page} of ${s.total}` : "";
    q(".dv-level").textContent = ZOOM_WORDS[s.zoom] ?? "";
    q<HTMLButtonElement>('[data-dvi="zoom-out"]').disabled = !s.total || s.zoom <= 0;
    q<HTMLButtonElement>('[data-dvi="zoom-in"]').disabled = !s.total || s.zoom >= ZOOMS.length - 1;
  };
  const say = (html: string | null, trouble = false): void => {
    q(".dv-msg").hidden = html === null;
    q(".dv-msg").innerHTML = html ?? "";
    box.toggleAttribute("data-trouble", trouble);
  };
  const begin = async (page: number): Promise<void> => {
    view?.destroy();
    const v = new PdfView(q(".dv-iscroll"), { url: target.url, maxFit: Number.POSITIVE_INFINITY, onState: (s) => { if (view === v) paint(s); } });
    view = v;
    q(".dv-iscroll").hidden = false;
    say("<p>Opening the file…</p>");
    try {
      await v.open(page);
      if (v.dead) return;
      say(null);
    } catch {
      if (v.dead || gone) return;
      v.destroy();
      q(".dv-iscroll").hidden = true;
      const status = await statusOf(target.url);
      if (view === v && !gone) say(troubleHtml(status, target.url, "pdf", opts.title), true);
    }
  };

  box.addEventListener("click", (e) => {
    const b = (e.target as Element).closest<HTMLElement>("[data-dvi], [data-dv]");
    if (!b) return;
    if (b.dataset.dv === "retry") return void begin(opts.page ?? target.page ?? 1);
    switch (b.dataset.dvi) {
      case "zoom-in": return view?.zoomBy(1);
      case "zoom-out": return view?.zoomBy(-1);
      case "full": return show({ url: target.url, kind: "pdf", page: view?.ready ? view.page : opts.page ?? target.page ?? 1, opener: b, ...(opts.title ? { title: opts.title } : {}) }, !!opts.title);
    }
  });
  void begin(opts.page ?? target.page ?? 1);

  return {
    destroy(): void {
      gone = true;
      view?.destroy();
      view = null;
      box.remove();
    },
  };
}
